/**
 * Asset Configuration
 * Centralized configuration for all asset paths with security validation
 */

// Environment variables with fallbacks
const ASSET_BASE_URL = process.env.REACT_APP_ASSET_BASE_URL || '';
const LOGO_FILENAME = process.env.REACT_APP_LOGO_FILENAME || 'AFF_logo.jpg';
const FAVICON_FILENAME = process.env.REACT_APP_FAVICON_FILENAME || 'favicon.ico';

// Allowed file extensions for security
const ALLOWED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.svg', '.webp'];
const ALLOWED_ICON_EXTENSIONS = ['.ico', '.png', '.svg'];

/**
 * Validates file path to prevent directory traversal attacks
 * @param {string} filename - The filename to validate
 * @param {string[]} allowedExtensions - Array of allowed file extensions
 * @returns {boolean} - True if valid, false otherwise
 */
const validateFilePath = (filename, allowedExtensions) => {
    if (!filename || typeof filename !== 'string') {
        return false;
    }

    // Check for directory traversal attempts
    if (filename.includes('..') || filename.includes('\\') || filename.includes('//')) {
        console.warn('Security warning: Potential directory traversal attempt detected:', filename);
        return false;
    }

    // Check for null bytes
    if (filename.includes('\0')) {
        console.warn('Security warning: Null byte detected in filename:', filename);
        return false;
    }

    // Check file extension
    const extension = filename.toLowerCase().substring(filename.lastIndexOf('.'));
    if (!allowedExtensions.includes(extension)) {
        console.warn('Security warning: Invalid file extension:', extension);
        return false;
    }

    // Check filename length (reasonable limit)
    if (filename.length > 255) {
        console.warn('Security warning: Filename too long:', filename);
        return false;
    }

    return true;
};

/**
 * Safely constructs asset path with validation
 * @param {string} filename - The filename to construct path for
 * @param {string[]} allowedExtensions - Array of allowed file extensions
 * @returns {string|null} - Safe asset path or null if invalid
 */
const getSafeAssetPath = (filename, allowedExtensions) => {
    if (!validateFilePath(filename, allowedExtensions)) {
        console.error('Invalid asset path:', filename);
        return null;
    }

    // Ensure filename starts with / for public directory access
    const safeFilename = filename.startsWith('/') ? filename : `/${filename}`;

    // Combine with base URL if provided
    return ASSET_BASE_URL ? `${ASSET_BASE_URL}${safeFilename}` : safeFilename;
};

/**
 * Asset configuration object
 */
export const assetConfig = {
    // Logo configuration
    logo: {
        filename: LOGO_FILENAME,
        getPath: () => getSafeAssetPath(LOGO_FILENAME, ALLOWED_IMAGE_EXTENSIONS),
        alt: 'AFF Logo',
        fallback: '🏛️' // Emoji fallback if image fails to load
    },

    // Favicon configuration
    favicon: {
        filename: FAVICON_FILENAME,
        getPath: () => getSafeAssetPath(FAVICON_FILENAME, ALLOWED_ICON_EXTENSIONS),
        fallback: '📄'
    },

    // Manifest configuration
    manifest: {
        logo192: {
            filename: 'AFF_logo.jpg', // Keep existing for manifest.json compatibility
            getPath: () => getSafeAssetPath('AFF_logo.jpg', ALLOWED_IMAGE_EXTENSIONS)
        },
        favicon: {
            filename: 'favicon.ico',
            getPath: () => getSafeAssetPath('favicon.ico', ALLOWED_ICON_EXTENSIONS)
        }
    },

    // Security settings
    security: {
        allowedImageExtensions: ALLOWED_IMAGE_EXTENSIONS,
        allowedIconExtensions: ALLOWED_ICON_EXTENSIONS,
        maxFilenameLength: 255,
        validatePath: validateFilePath
    }
};

/**
 * Utility function to get logo path with fallback
 * @returns {string} - Safe logo path or fallback
 */
export const getLogoPath = () => {
    const logoPath = assetConfig.logo.getPath();
    return logoPath || assetConfig.logo.fallback;
};

/**
 * Utility function to get favicon path with fallback
 * @returns {string} - Safe favicon path or fallback
 */
export const getFaviconPath = () => {
    const faviconPath = assetConfig.favicon.getPath();
    return faviconPath || assetConfig.favicon.fallback;
};

/**
 * Development helper to log current asset configuration
 */
export const logAssetConfig = () => {
    if (process.env.NODE_ENV === 'development') {
        console.log('Asset Configuration:', {
            baseUrl: ASSET_BASE_URL,
            logoFilename: LOGO_FILENAME,
            faviconFilename: FAVICON_FILENAME,
            logoPath: getLogoPath(),
            faviconPath: getFaviconPath()
        });
    }
};

export default assetConfig;
